import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  getDiscountDisplayAmountCents,
  getMatchingAppliedDiscountCents,
} from "../../../utils/getDiscountDisplayAmountCents";
import {
  LineItemType,
  useGetLineItemsAndPriceForRound_Round$key,
} from "./__generated__/useGetLineItemsAndPriceForRound_Round.graphql";

type Discount = {
  id: string;
  name: string;
  appliedAmount: number;
  lineItemGroupId: string | null | undefined;
};

type LineItem = {
  id: string;
  name: string;
  price: number;
  type: LineItemType;
  __typename: string;
};

type LineItemWithGroupId = LineItem & {
  lineItemGroupId: string | null | undefined;
};

type LineItemGroupForRound = {
  id: string;
  lineItems: LineItem[];
  discounts: Discount[];
};

export const useGetLineItemsAndPriceForRound = ({
  fragmentRef,
  roundId,
}: {
  fragmentRef: useGetLineItemsAndPriceForRound_Round$key | null;
  roundId: string;
}): {
  lineItemsForRound: LineItemWithGroupId[] | undefined;
  lineItemGroupsForRound: LineItemGroupForRound[];
  totalPriceForRound: number | undefined;
  discountsByLineItemGroupId: Record<string, Discount[]>;
} => {
  const data = useFragment(
    useGetLineItemsAndPriceForRoundFragmentString,
    fragmentRef
  );

  const relevantLineItemGroupsForRound = data?.booking?.orders?.edges?.flatMap(
    (order) =>
      order?.node?.lineItemGroups?.filter((lineItemGroup) => {
        return lineItemGroup?.round?.id === roundId;
      })
  );

  const lineItemsForRound: LineItemWithGroupId[] | undefined =
    relevantLineItemGroupsForRound?.flatMap((lineItemGroup) => {
      const lineItems: LineItemWithGroupId[] = [];

      lineItemGroup?.lineItems?.forEach((lineItem) => {
        if (
          !lineItem ||
          !lineItem.id ||
          !lineItem.__typename ||
          !lineItem.name ||
          lineItem.price == null ||
          !lineItem.type
        ) {
          return;
        }

        lineItems.push({
          id: lineItem.id,
          __typename: lineItem.__typename,
          name: lineItem.name,
          price: lineItem.price,
          type: lineItem.type,
          lineItemGroupId: lineItemGroup?.id ?? null,
        });
      });

      return lineItems;
    });

  const discountsByLineItemGroupId =
    data?.booking?.orders?.edges?.reduce<Record<string, Discount[]>>(
      (accumulator, orderEdge) => {
        const discounts =
          orderEdge?.node?.orderDiscounts?.edges?.map(
            (discountEdge) => discountEdge?.node
          ) ?? [];
        const groupsById = new Map(
          (orderEdge?.node?.lineItemGroups ?? []).flatMap((group) => {
            if (!group?.id) {
              return [];
            }

            return [[group.id, group]];
          })
        );

        discounts.forEach((discount) => {
          if (!discount?.lineItemGroupId) {
            return;
          }

          const existingDiscounts = accumulator[discount.lineItemGroupId] ?? [];
          const groupLineItems =
            groupsById.get(discount.lineItemGroupId)?.lineItems ?? [];

          accumulator[discount.lineItemGroupId] = [
            ...existingDiscounts,
            {
              id: discount.id,
              name: discount.name,
              appliedAmount: getDiscountDisplayAmountCents(
                discount,
                getMatchingAppliedDiscountCents(
                  discount.squareExternalId,
                  groupLineItems
                )
              ),
              lineItemGroupId: discount.lineItemGroupId,
            },
          ];
        });

        return accumulator;
      },
      {}
    ) ?? {};

  const totalDiscountsForRound = relevantLineItemGroupsForRound?.reduce(
    (accumulator, lineItemGroup) => {
      if (!lineItemGroup?.id) {
        return accumulator;
      }

      const groupDiscountTotal =
        discountsByLineItemGroupId[lineItemGroup.id]?.reduce(
          (groupAccumulator, discount) =>
            groupAccumulator + (discount.appliedAmount ?? 0),
          0
        ) ?? 0;

      return accumulator + groupDiscountTotal;
    },
    0
  );

  const totalPriceForRound = lineItemsForRound?.reduce((acc: number, cur) => {
    return acc + (cur?.price ?? 0);
  }, 0);

  const totalPriceForRoundAfterDiscounts =
    totalPriceForRound != null
      ? totalPriceForRound - (totalDiscountsForRound ?? 0)
      : totalPriceForRound;

  const lineItemGroupsForRound =
    relevantLineItemGroupsForRound?.reduce<LineItemGroupForRound[]>(
      (accumulator, lineItemGroup) => {
        if (!lineItemGroup?.id) {
          return accumulator;
        }

        const lineItems: LineItem[] = [];

        lineItemGroup.lineItems?.forEach((lineItem) => {
          if (
            !lineItem ||
            !lineItem.id ||
            !lineItem.__typename ||
            !lineItem.name ||
            lineItem.price == null ||
            !lineItem.type
          ) {
            return;
          }

          lineItems.push({
            id: lineItem.id,
            __typename: lineItem.__typename,
            name: lineItem.name,
            price: lineItem.price,
            type: lineItem.type,
          });
        });

        accumulator.push({
          id: lineItemGroup.id,
          lineItems,
          discounts: discountsByLineItemGroupId[lineItemGroup.id] ?? [],
        });

        return accumulator;
      },
      []
    ) ?? [];

  return {
    lineItemsForRound,
    lineItemGroupsForRound,
    totalPriceForRound: totalPriceForRoundAfterDiscounts,
    discountsByLineItemGroupId,
  };
};

export const useGetLineItemsAndPriceForRoundFragmentString = graphql`
  fragment useGetLineItemsAndPriceForRound_Round on Round {
    ... on CustomerRound {
      paymentStatus
    }
    booking {
      orders {
        edges {
          node {
            status
            id
            lineItemGroups {
              id
              type
              round {
                id
              }
              lineItems {
                id
                __typename
                type
                name
                price
                ... on RoundLineItem {
                  appliedDiscounts {
                    amountCents
                    squareExternalDiscountId
                  }
                }
                ... on BookingLineItem {
                  appliedDiscounts {
                    amountCents
                    squareExternalDiscountId
                  }
                }
              }
            }
            orderDiscounts {
              edges {
                node {
                  id
                  name
                  appliedAmount
                  amount
                  squareExternalId
                  lineItemGroupId
                }
              }
            }
          }
        }
      }
    }
  }
`;
